import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { DashboardLayout } from "../layouts/DashboardLayout";
import { Card } from "../components/ui/Card";
import { Input } from "../components/ui/Input";
import { Button } from "../components/ui/Button";
import { useToast } from "../components/ui/ToastContainer";
import { courseService } from "../services/courseService";
import { moduleService } from "../services/moduleService";
import { parseApiError, formatValidationErrors } from "../utils/errorHandler";
import { ArrowLeft, Loader2, Save } from "lucide-react";

interface FormData {
  title: string;
  order: number;
  level: "beginner" | "intermediate" | "advanced";
  isPublished: boolean;
  completionCriteria: "all-lessons" | "quiz-pass" | "none";
  completionPercent: number;
}

export default function AddEditModulePage() {
  const navigate = useNavigate();
  const { id: courseId, moduleId } = useParams<{
    id: string;
    moduleId?: string;
  }>();
  const isEditMode = Boolean(moduleId);

  const [courseName, setCourseName] = useState("");
  const [isFetching, setIsFetching] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string[]>
  >({});
  const { showSuccess, showError } = useToast();

  const [formData, setFormData] = useState<FormData>({
    title: "",
    order: 1,
    level: "beginner",
    isPublished: false,
    completionCriteria: "all-lessons",
    completionPercent: 100,
  });

  const inputClassName =
    "w-full px-4 py-3 bg-white border border-[#DCE0E5] rounded-[8px] text-[14px] text-[#102040] placeholder:text-[#706F6F] focus:outline-none focus:ring-2 focus:ring-[#e07828]/20 focus:border-[#e07828] transition-all";

  const selectClassName =
    "w-full px-4 py-3 bg-white border border-[#DCE0E5] rounded-[8px] text-[14px] text-[#102040] focus:outline-none focus:ring-2 focus:ring-[#e07828]/20 focus:border-[#e07828] transition-all appearance-none cursor-pointer";

  // Fetch course details
  useEffect(() => {
    const fetchCourse = async () => {
      if (!courseId) return;
      try {
        const response = await courseService.getCourseById(courseId);
        const course = response.course;
        setCourseName(course?.title || "");

        // If creating new module, default level to course level
        if (!isEditMode && course?.level) {
          setFormData((prev) => ({
            ...prev,
            level: (course.level as any) || "beginner",
          }));
        }
      } catch (err) {
        console.error("Failed to fetch course:", err);
      }
    };
    fetchCourse();
  }, [courseId, isEditMode]);

  // Fetch module data if editing
  const fetchModule = useCallback(async () => {
    if (!moduleId) return;
    setIsFetching(true);
    try {
      const response = await moduleService.getModuleById(moduleId);
      const module = response.module as any;
      setFormData({
        title: module.title || "",
        order: module.order || 1,
        level: module.level || "beginner",
        isPublished: module.isPublished || false,
        completionCriteria: module.completionCriteria || "all-lessons",
        completionPercent: module.completionPercent || 100,
      });
    } catch (err: any) {
      console.error("Failed to fetch module:", err);
      const apiError = parseApiError(err);
      setError(apiError.message);
      showError(apiError.message, "Failed to Load Module");
    } finally {
      setIsFetching(false);
    }
  }, [moduleId]);

  useEffect(() => {
    if (isEditMode) {
      fetchModule();
    }
  }, [isEditMode, fetchModule]);

  // Auto-set order for new modules
  useEffect(() => {
    const fetchModules = async () => {
      if (isEditMode || !courseId) return;
      try {
        const response = await moduleService.getModulesByCourse(courseId);
        const modules = response.modules || [];
        const maxOrder = modules.reduce(
          (max: number, m: any) => Math.max(max, m.order || 0),
          0
        );
        setFormData((prev) => ({ ...prev, order: maxOrder + 1 }));
      } catch (err) {
        console.error("Failed to fetch modules for order:", err);
      }
    };
    fetchModules();
  }, [courseId, isEditMode]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : type === "number"
          ? Number(value)
          : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    setValidationErrors({});

    try {
      const payload = {
        title: formData.title,
        order: formData.order,
        level: formData.level,
        isPublished: formData.isPublished,
        completionCriteria: formData.completionCriteria,
        completionPercent: formData.completionPercent,
        course: courseId!,
      };

      if (isEditMode && moduleId) {
        await moduleService.updateModule(moduleId, payload);
        showSuccess("Module updated successfully", "Success");
      } else {
        await moduleService.createModule(payload);
        showSuccess("Module created successfully", "Success");
      }

      navigate(`/courses/${courseId}/structure`);
    } catch (err: any) {
      console.error("Failed to save module:", err);
      const apiError = parseApiError(err);

      if (apiError.errors && Object.keys(apiError.errors).length > 0) {
        setValidationErrors(apiError.errors);
        setError("Please fix the validation errors below.");
      } else {
        setError(apiError.message);
      }

      showError(
        apiError.message,
        isEditMode ? "Update Failed" : "Create Failed"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isFetching) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#e07828]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <form onSubmit={handleSubmit} className="w-full">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-6">
          <div className="flex items-center gap-4">
            <Link
              to={`/courses/${courseId}/structure`}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft size={20} className="text-gray-600" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-[#102040]">
                {isEditMode ? "Edit Module" : "Add New Module"}
              </h1>
              <p className="text-[#706F6F] text-sm">
                Course: {courseName || "Loading..."}
              </p>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 font-medium">{error}</p>
            {Object.keys(validationErrors).length > 0 && (
              <ul className="mt-2 ml-4 list-disc space-y-1 text-sm text-red-600">
                {formatValidationErrors(validationErrors).map((err, idx) => (
                  <li key={idx}>
                    <span className="font-medium">{err.field}:</span>{" "}
                    {err.messages.join(", ")}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <Card className="p-4 sm:p-6 space-y-6">
          {/* Title */}
          <div>
            <label className="text-[14px] font-normal text-[#102040] tracking-[0.5px] mb-2 block">
              Module Title <span className="text-[#FF0000]">*</span>
            </label>
            <Input
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Enter module title"
              required
              className={inputClassName}
            />
          </div>

          {/* Order */}
          <div>
            <label className="text-[14px] font-normal text-[#102040] tracking-[0.5px] mb-2 block">
              Order <span className="text-[#FF0000]">*</span>
            </label>
            <Input
              type="number"
              name="order"
              value={formData.order}
              onChange={handleChange}
              placeholder="1"
              required
              min={1}
              className={inputClassName}
            />
            <p className="text-[12px] text-[#706F6F] mt-1">
              Determines the display order of modules
            </p>
          </div>

          {/* Level */}
          <div>
            <label className="text-[14px] font-normal text-[#102040] tracking-[0.5px] mb-2 block">
              Module Level <span className="text-[#FF0000]">*</span>
            </label>
            <select
              name="level"
              value={formData.level}
              onChange={handleChange}
              required
              className={selectClassName}
            >
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
            </select>
          </div>

          {/* Completion Criteria */}
          <div>
            <label className="text-[14px] font-normal text-[#102040] tracking-[0.5px] mb-2 block">
              Completion Criteria
            </label>
            <select
              name="completionCriteria"
              value={formData.completionCriteria}
              onChange={handleChange}
              className={selectClassName}
            >
              <option value="all-lessons">Complete all lessons</option>
              {/* <option value="quiz-pass">Pass Quiz</option> */}
              <option value="none">No requirement</option>
            </select>
          </div>

          {/* Published */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="isPublished"
              name="isPublished"
              checked={formData.isPublished}
              onChange={handleChange}
              className="w-5 h-5 rounded border-gray-300 text-[#e07828] focus:ring-[#e07828]"
            />
            <label htmlFor="isPublished" className="text-[14px] text-[#102040]">
              Publish this module
            </label>
          </div>
        </Card>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row justify-end gap-4 mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(`/courses/${courseId}/structure`)}
            disabled={isSubmitting}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={isSubmitting}
            className="w-full sm:w-auto"
          >
            {isSubmitting ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {isEditMode ? "Update Module" : "Create Module"}
          </Button>
        </div>
      </form>
    </DashboardLayout>
  );
}
